import React, {useEffect, useRef} from 'react';
import Typography from '@mui/material/Typography';
import {EditorState, Plugin} from 'prosemirror-state';
import {Decoration, DecorationSet, EditorView} from 'prosemirror-view';
import {editorSchema} from '../../../../../components/prosemirror-editor/core/schema';
import {generateUUID} from '../../../../../components/prosemirror-editor/helpers/utils';
import {isMarkOpenable, openCitationRefSource} from '../../../../../components/prosemirror-editor/components/CitationRefCard';
import {resolveForecastSourceAttrs} from './ForecastCitations';
import '../../../../../components/prosemirror-editor/styles/metaMarks.css';
import './ForecastPreview.css';

/**
 * MINSKY-16818 (ticket 03): build a single preview paragraph node — plain text plus, for each
 * resolvable cited-source key, a `reference`-marked span (same mark/styling the real editor
 * uses) covering the WHOLE text. This mirrors `buildCitationMarkAttrsList`'s numbering lookup
 * (ticket 02/04) but produces schema-native marks rather than `citationRef` text nodes, since the
 * preview isn't inserting into the live document — it's a standalone read-only doc.
 * <p>
 * MINSKY-16818: when `bold` is true, the whole text also carries the schema's `strong` mark,
 * alongside any `reference` marks — used only for the predictions preview, since the summary and
 * key drivers keep their previous (non-bold) rendering.
 * <p>
 * MINSKY-16818 (ticket 02): also returns `attrsByUid`, resolving each mark's uid to the SAME
 * open-source attrs shape `openCitationRefSource` expects, so the decoration plugin can make the
 * "[n]" widget clickable — reusing the in-document popover's open logic rather than a copy.
 *
 * Returns `{paragraph, numbersByUid, attrsByUid}`, or null for empty/missing text.
 */
export function buildForecastPreviewParagraph(schema, text, citedSources, sourcesByKey, numberByKey, bold = false) {
    if (!text) return null;

    const referenceMarkType = schema.marks.reference;
    const numbersByUid = new Map();
    const attrsByUid = new Map();

    const marks = (citedSources || [])
        .map((key) => resolveForecastSourceAttrs(key, sourcesByKey, numberByKey))
        .filter(Boolean)
        .map((attrs) => {
            const uid = generateUUID();
            numbersByUid.set(uid, [attrs.number]);
            attrsByUid.set(uid, attrs);
            return referenceMarkType.create({uid});
        });

    // MINSKY-16818: only the predictions preview renders bold; summary/key drivers stay plain.
    if (bold) {
        marks.push(schema.marks.strong.create());
    }

    const textNode = schema.text(text, marks);
    const paragraph = schema.nodes.paragraph.create(null, textNode);
    return {paragraph, numbersByUid, attrsByUid};
}

/**
 * Local citation-number widget for the Forecast preview — visually matches
 * `referenceCitation/decorations.js`'s `createCitationWidget` (same `.reference-citation-number`
 * class/shape) but, unlike that shared one, optionally becomes a clickable/keyboard-operable
 * affordance (ticket 02) when `onOpen` is given. Kept local rather than extending the shared
 * widget so the live in-document editor's citation rendering is untouched.
 */
function createForecastCitationWidget(number, onOpen) {
    const span = document.createElement('sup');
    span.className = 'reference-citation-number';
    span.style.cssText = `
        color: #1976d2;
        font-weight: 600;
        font-size: 0.75em;
        margin-left: 1px;
        user-select: none;
        cursor: ${onOpen ? 'pointer' : 'inherit'};
    `;
    span.textContent = `[${number}]`;
    if (onOpen) {
        span.setAttribute('role', 'button');
        span.setAttribute('tabindex', '0');
        span.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpen();
        });
        span.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen();
            }
        });
    }
    return span;
}

/**
 * Local, standalone analogue of `referenceCitation/decorations.js`'s `createCitationDecorations`
 * — no Yjs, no store lookup. Numbers come straight from the `numbersByUid` map built alongside
 * the preview doc, reusing the SAME widget shape (`createCitationWidget`, `.reference-citation-number`)
 * as the live editor for visual parity. MINSKY-16818 (ticket 02): each widget also becomes
 * clickable — opening the same source as its corresponding footnote-list line — when `attrsByUid`
 * resolves the mark to an openable source (same `isMarkOpenable` gate `CitationRefCard` uses for
 * its "Open source" link, so an unopenable source never renders a dead clickable affordance).
 */
function forecastPreviewCitationPlugin(numbersByUid, attrsByUid) {
    return new Plugin({
        props: {
            decorations(state) {
                const referenceMarkType = state.schema.marks.reference;
                if (!referenceMarkType) return DecorationSet.empty;

                const decorations = [];
                state.doc.descendants((node, pos) => {
                    if (!node.isText) return;
                    node.marks.forEach((mark) => {
                        if (mark.type !== referenceMarkType) return;
                        const numbers = numbersByUid.get(mark.attrs.uid);
                        if (!numbers?.length) return;
                        const attrs = attrsByUid?.get(mark.attrs.uid);
                        const onOpen = attrs && isMarkOpenable(attrs) ? () => openCitationRefSource(attrs) : null;
                        decorations.push(
                            Decoration.widget(pos + node.nodeSize, createForecastCitationWidget(numbers[0], onOpen), {
                                side: -1,
                                key: `forecast-preview-citation-${mark.attrs.uid}`,
                            }),
                        );
                    });
                });
                return DecorationSet.create(state.doc, decorations);
            },
        },
    });
}

/**
 * Renders one preview text (summary / a key driver / a prediction statement) as a small,
 * read-only ProseMirror `EditorView` on the shared editor schema — replacing the previous plain
 * MUI `<Typography>` + `CitationMarkers` popover. Cited-source spans use the `reference` mark's
 * existing `.meta-mark.meta-reference` styling; citation numbers are drawn by a local decoration
 * seeded from the forecast's own `sourcesByKey`/`numberByKey` numbering — no hover/click popover,
 * no Yjs document, no margin rail. MINSKY-16818 (ticket 02): each citation number IS clickable,
 * opening the same source as the per-item footnote list beneath this text.
 * <p>
 * MINSKY-16818: `bold` (default `false`) renders the text with the schema's `strong` mark —
 * only the predictions call site passes `bold={true}`; the summary and key drivers keep their
 * previous (non-bold) rendering.
 */
export function ForecastPreviewText({text, citedSources = [], sourcesByKey, numberByKey, bold = false}) {
    const mountRef = useRef(null);
    const viewRef = useRef(null);

    useEffect(() => {
        if (!mountRef.current) return undefined;

        const built = buildForecastPreviewParagraph(editorSchema, text, citedSources, sourcesByKey, numberByKey, bold);
        if (!built) return undefined;

        const {paragraph, numbersByUid, attrsByUid} = built;
        const doc = editorSchema.nodes.doc.create(null, paragraph);
        const state = EditorState.create({
            schema: editorSchema,
            doc,
            plugins: [forecastPreviewCitationPlugin(numbersByUid, attrsByUid)],
        });

        const view = new EditorView(mountRef.current, {
            state,
            editable: () => false,
        });
        viewRef.current = view;

        return () => {
            view.destroy();
            viewRef.current = null;
        };
        // Rebuilt whenever the underlying preview content/citations change — this is a small,
        // immutable-per-render preview doc, so full recreation (rather than an incremental
        // dispatch) keeps this component simple.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, citedSources, sourcesByKey, numberByKey, bold]);

    if (!text) return null;

    return <Typography ref={mountRef} className="forecast-preview-text"/>;
}
